import { Fragment, useMemo, useState } from "react";
import { Button, OverlayTrigger, Popover, Table } from "react-bootstrap";
import {
  Check,
  ChevronDown,
  ChevronRight,
  InfoCircle,
  Pencil,
  JournalText,
} from "react-bootstrap-icons";
import { Currency } from "../../../../lib/components/Currency";
import {
  FormattedDate,
  FormattedDateTime,
} from "../../../../lib/components/FormattedDate";
import { EmptyRow } from "./EmptyState";
import { RecordActions } from "./RecordActions";
import {
  buildBonusSelectionRow,
  getBonusSelectionKey,
  isBonusSelectable,
} from "../utils/recordSelection";

const COL_COUNT = 7;

function statusClass(status) {
  if (status === "approved") return "status-text status-text--approved";
  if (status === "paid") return "status-text status-text--paid";
  if (status === "pending" || status === "in_review")
    return "status-text status-text--pending";
  if (status === "rejected") return "status-text status-text--rejected";
  return "status-text";
}

function sumBonusAdjustments(adjustments) {
  return (adjustments ?? []).reduce(
    (acc, adjustment) =>
      acc + (adjustment.adjustmentAmount ?? adjustment.adjustedAmount ?? 0),
    0,
  );
}

export function BonusesSubTable({
  records,
  agentKey,
  onAdjust,
  onReject,
  onApprove,
  onRemove,
  onReopen,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  isSelectionDisabled,
  areRecordActionsDisabled,
  approvingRecordKeys,
  removingRecordKeys,
}) {
  const [expandedRows, setExpandedRows] = useState(() => new Set());

  const selectableRows = useMemo(
    () =>
      records
        .map((r, i) => buildBonusSelectionRow(agentKey, r, i))
        .filter((r) => r.selectable),
    [agentKey, records],
  );

  const selectableCount = selectableRows.length;
  const selectedCount = selectableRows.filter((r) =>
    selectedKeys.has(r.key),
  ).length;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = () => onToggleSelectAll(selectableRows, !allSelected);

  function toggleRow(rowKey) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  }

  return (
    <Table
      hover
      responsive
      className="data-table data-table--compact data-table--interactive align-middle"
      striped
    >
      {records.length > 0 && (
        <thead>
          <tr>
            <th className="col-select" aria-label="Select all">
              {selectableCount > 0 && (
                <button
                  type="button"
                  role="checkbox"
                  className={`record-select-checkbox ${allSelected || someSelected ? "is-on" : ""}`.trim()}
                  aria-label="Select all bonus rows"
                  aria-checked={someSelected ? "mixed" : allSelected}
                  onClick={toggleAll}
                  disabled={selectableCount === 0 || isSelectionDisabled}
                >
                  {someSelected ? (
                    <span className="record-select-dash" />
                  ) : allSelected ? (
                    <Check size={11} strokeWidth={3.2} />
                  ) : null}
                </button>
              )}
            </th>
            <th className="col-toggle" aria-label="Details" />
            <th>Bonus rule</th>
            <th>Period</th>
            <th>Status</th>
            <th className="col-money">Amount</th>
            <th className="col-actions col-sticky">Actions</th>
          </tr>
        </thead>
      )}
      <tbody>
        {records.length === 0 ? (
          <EmptyRow colSpan={COL_COUNT} text="No bonus records." />
        ) : (
          records.map((r, i) => {
            const isFinalized =
              r.status === "approved" ||
              r.status === "paid" ||
              r.status === "declined";
            const originalAmount = r.amount ?? 0;
            const adjustmentTotal = sumBonusAdjustments(r.adjustments);
            const rowAmount = originalAmount + adjustmentTotal;
            const recordActionKey = `bonus:${r.bonusRecordId}`;
            const rowKey = `bonus-${r.bonusRecordId ?? r.created ?? i}`;
            const adjustments = r.adjustments ?? [];
            const hasAdjustments = adjustments.length > 0;
            const hasDetails = hasAdjustments || Boolean(r.notes?.trim());
            const showAmountTooltip = hasAdjustments || adjustmentTotal !== 0;
            const isExpanded = expandedRows.has(rowKey);
            const popoverId = `bonus-amount-breakdown-${r.bonusRecordId ?? i}`;
            const selectionId = getBonusSelectionKey(agentKey, r, i);
            const isSelected = selectedKeys.has(selectionId);
            const isSelectable = isBonusSelectable(r);

            return (
              <Fragment key={rowKey}>
                <tr>
                  <td className="col-select">
                    <button
                      type="button"
                      role="checkbox"
                      className={`record-select-checkbox ${isSelected ? "is-on" : ""} ${!isSelectable ? "is-disabled" : ""}`.trim()}
                      aria-label={`Select bonus ${r.bonusRuleName || r.bonusRecordId || ""}`}
                      aria-checked={isSelected}
                      onClick={() =>
                        onToggleSelect(buildBonusSelectionRow(agentKey, r, i))
                      }
                      disabled={!isSelectable || isSelectionDisabled}
                    >
                      {isSelected ? (
                        <Check size={11} strokeWidth={3.2} />
                      ) : null}
                    </button>
                  </td>
                  <td className="col-toggle">
                    {hasDetails && (
                      <Button
                        variant="link"
                        className="btn-toggle"
                        onClick={() => toggleRow(rowKey)}
                        aria-label={
                          isExpanded
                            ? "Collapse adjustments"
                            : "Expand adjustments"
                        }
                      >
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </Button>
                    )}
                  </td>
                  <td>{r.bonusRuleName || "-"}</td>
                  <td>
                    {r.periodStart ? (
                      <FormattedDate date={r.periodStart} />
                    ) : (
                      "-"
                    )}
                    {" – "}
                    {r.periodEnd ? <FormattedDate date={r.periodEnd} /> : "-"}
                  </td>
                  <td>
                    <span className={statusClass(r.status)}>
                      {r.status || "-"}
                    </span>
                  </td>
                  <td className="col-money tnum col-money--emphasis">
                    {showAmountTooltip ? (
                      <span className="bonus-amount-wrap">
                        <Currency amount={rowAmount} />
                        <OverlayTrigger
                          placement="top"
                          trigger={["hover", "focus"]}
                          overlay={
                            <Popover
                              id={popoverId}
                              className="bonus-amount-popover"
                            >
                              <Popover.Body>
                                <div className="bonus-amount-tooltip-grid">
                                  <span>Original</span>
                                  <span>
                                    <Currency amount={originalAmount} />
                                  </span>
                                  <span>Adjustments</span>
                                  <span>
                                    <Currency amount={adjustmentTotal} />
                                  </span>
                                </div>
                                <div className="bonus-amount-tooltip-divider" />
                                <div className="bonus-amount-tooltip-grid bonus-amount-tooltip-total">
                                  <span>Total</span>
                                  <span>
                                    <Currency amount={rowAmount} />
                                  </span>
                                </div>
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <button
                            type="button"
                            className="bonus-amount-trigger"
                            aria-label="Show amount breakdown"
                          >
                            <InfoCircle size={13} aria-hidden="true" />
                          </button>
                        </OverlayTrigger>
                      </span>
                    ) : (
                      <span className="bonus-amount-wrap">
                        <Currency amount={rowAmount} />
                        <span
                          className="bonus-amount-trigger-spacer"
                          aria-hidden="true"
                        />
                      </span>
                    )}
                  </td>
                  <td className="col-actions col-sticky">
                    <RecordActions
                      isFinalized={isFinalized}
                      isApproving={approvingRecordKeys.has(recordActionKey)}
                      isRemoving={removingRecordKeys.has(recordActionKey)}
                      isDisabled={areRecordActionsDisabled}
                      onApprove={
                        onApprove
                          ? () =>
                              onApprove({
                                kind: "bonus",
                                id: r.bonusRecordId,
                                label: r.bonusRuleName || "Bonus",
                              })
                          : undefined
                      }
                      onAdjust={() =>
                        onAdjust({
                          kind: "bonus",
                          id: r.bonusRecordId,
                          label: r.bonusRuleName || "Bonus",
                          effective: rowAmount,
                        })
                      }
                      onReject={() =>
                        onReject({
                          kind: "bonus",
                          id: r.bonusRecordId,
                          label: r.bonusRuleName || "Bonus",
                        })
                      }
                      onRemove={
                        onRemove
                          ? () =>
                              onRemove({
                                kind: "bonus",
                                id: r.bonusRecordId,
                                label: r.bonusRuleName || "Bonus",
                              })
                          : undefined
                      }
                      onReopen={
                        onReopen
                          ? () =>
                              onReopen({
                                kind: "bonus",
                                id: r.bonusRecordId,
                                label: r.bonusRuleName || "Bonus",
                              })
                          : undefined
                      }
                    />
                  </td>
                </tr>
                {isExpanded && hasDetails && (
                  <BonusDetailsRow
                    colSpan={COL_COUNT}
                    adjustments={adjustments}
                    notes={r.notes}
                  />
                )}
              </Fragment>
            );
          })
        )}
      </tbody>
    </Table>
  );
}

function BonusDetailsRow({ colSpan, adjustments, notes }) {
  return (
    <tr className="details-row">
      <td colSpan={colSpan}>
        {adjustments.length > 0 && (
          <DetailSection
            title="Bonus adjustments"
            icon={<Pencil size={12} aria-hidden="true" />}
          >
            <Table
              responsive
              className="data-table data-table--nested align-middle"
            >
              <thead>
                <tr>
                  <th>User</th>
                  <th>Date</th>
                  <th>Reason</th>
                  <th className="col-money">Amount</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a, i) => (
                  <tr
                    key={
                      a.bonusRecordAdjustmentId ??
                      a.created ??
                      `${a.reason ?? "adjustment"}-${i}`
                    }
                  >
                    <td>{a.createdBy || "-"}</td>
                    <td>
                      {a.created ? <FormattedDateTime date={a.created} /> : "-"}
                    </td>
                    <td>{a.reason || "-"}</td>
                    <td className="col-money tnum col-money--emphasis">
                      <Currency
                        amount={a.adjustmentAmount ?? a.adjustedAmount ?? 0}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </DetailSection>
        )}
        {notes && (
          <DetailSection
            title="Notes"
            icon={<JournalText size={12} aria-hidden="true" />}
          >
            <p>{notes}</p>
          </DetailSection>
        )}
      </td>
    </tr>
  );
}

function DetailSection({ title, icon, children }) {
  return (
    <div className="details-section">
      <div className="details-section-title">
        {icon && <span className="details-section-icon">{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
}
